import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthCheck } from '@/hooks/queries/useAuthQueries';
import { useAuthStore } from '@/stores/authStore';
import { localStorageUtils } from '@/utils/localStorage';

/**
 * Generic auth callback page
 * Used by backend to redirect after authentication/verification flows
 *
 * Query parameters:
 * - redirect: destination path (default: '/pools')
 * - checkLiveness: if 'true', checks is_liveness_verified and opens modal if not verified
 * - openModal: modal type to open ('verification' opens UserVerificationModal)
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setAuthState, setLivenessChecked } = useAuthStore();
  const hasSession = localStorageUtils.isSessionAuthenticated();

  // Get query parameters
  const redirectPath = searchParams.get('redirect') || '/pools';
  const checkLiveness = searchParams.get('checkLiveness') === 'true';

  // Manually trigger auth check
  const { data: authData, isLoading: authCheckLoading, status: authCheckStatus } = useAuthCheck();

  // Wait for auth check to complete
  const isAuthCheckPending = hasSession && (authCheckLoading || authCheckStatus === 'pending');

  // Use query data as source of truth if available, otherwise fall back to store
  const effectiveIsAuthenticated = hasSession && authData ? authData.authenticated : false;
  const effectiveUser = hasSession && authData && authData.authenticated ? authData.user : user;

  useEffect(() => {
    // Update store with latest auth data
    if (authData && authData.authenticated && authData.user) {
      setAuthState(authData.user, true);
    }
  }, [authData, setAuthState]);

  useEffect(() => {
    // If no session, redirect to home immediately
    if (!hasSession) {
      navigate('/');
      return;
    }

    // Don't proceed while auth check is in progress
    if (isAuthCheckPending) {
      return;
    }

    // If not authenticated, redirect to home
    if (!effectiveIsAuthenticated || !effectiveUser) {
      navigate('/');
      return;
    }

    // Build redirect URL with query parameters if needed
    const urlParams = new URLSearchParams();

    // Check liveness verification if requested
    if (checkLiveness) {
      setLivenessChecked(true);
      urlParams.set('openVerificationModal', 'true');
    }

    // Navigate to destination with query parameters
    const finalPath = urlParams.toString() ? `${redirectPath}?${urlParams.toString()}` : redirectPath;

    navigate(finalPath);
  }, [
    hasSession,
    isAuthCheckPending,
    effectiveIsAuthenticated,
    effectiveUser,
    navigate,
    redirectPath,
    checkLiveness,
    setLivenessChecked,
  ]);

  // Show loading screen while checking auth
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Verifying authentication...</p>
      </div>
    </div>
  );
}
